// src/pages/platform/PlatformFederationPage.tsx
// Platform federation management: peers, discovery, consent.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { usePlatform, usePlatformFederation, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';
import type { FederationTrustLevel } from '@core/types/platform';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TabId = 'peers' | 'discovery' | 'consent';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format ISO date to localized string. */
function formatDate(iso: string | undefined): string {
  if (!iso) return '--';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/** Relative time (e.g. "3 min ago"). */
function relativeTime(iso: string | undefined): string {
  if (!iso) return '--';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  } catch {
    return iso;
  }
}

/** Map FederationPeerStatus to Badge variant. */
function peerStatusVariant(status: string): string {
  switch (status) {
    case 'FEDERATION_PEER_STATUS_ACTIVE':
    case 'ACTIVE':
      return 'success';
    case 'FEDERATION_PEER_STATUS_PENDING':
    case 'PENDING':
      return 'warning';
    case 'FEDERATION_PEER_STATUS_SUSPENDED':
    case 'SUSPENDED':
      return 'error';
    case 'FEDERATION_PEER_STATUS_REVOKED':
    case 'REVOKED':
      return 'secondary';
    case 'FEDERATION_PEER_STATUS_ERROR':
    case 'ERROR':
      return 'error';
    default:
      return 'secondary';
  }
}

/** Human-readable peer status label. */
function peerStatusLabel(status: string): string {
  return (status ?? '')
    .replace('FEDERATION_PEER_STATUS_', '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

/** Map FederationTrustLevel to Badge variant. */
function trustLevelVariant(level: string): string {
  switch (level) {
    case 'FEDERATION_TRUST_LEVEL_FULL':
    case 'FULL':
      return 'success';
    case 'FEDERATION_TRUST_LEVEL_RESOURCE_LINK':
    case 'RESOURCE_LINK':
      return 'primary';
    case 'FEDERATION_TRUST_LEVEL_IDENTITY':
    case 'IDENTITY':
      return 'info';
    case 'FEDERATION_TRUST_LEVEL_CATALOG_ONLY':
    case 'CATALOG_ONLY':
      return 'secondary';
    default:
      return 'secondary';
  }
}

/** Human-readable trust level label. */
function trustLevelLabel(level: string): string {
  return (level ?? '')
    .replace('FEDERATION_TRUST_LEVEL_', '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

/** Map consent status to Badge variant. */
function consentStatusVariant(status: string): string {
  switch (status) {
    case 'GRANTED':
    case 'CONSENT_STATUS_GRANTED':
      return 'success';
    case 'REVOKED':
    case 'CONSENT_STATUS_REVOKED':
      return 'error';
    case 'EXPIRED':
    case 'CONSENT_STATUS_EXPIRED':
      return 'secondary';
    default:
      return 'secondary';
  }
}

/** Human-readable consent status label. */
function consentStatusLabel(status: string): string {
  return (status ?? '')
    .replace('CONSENT_STATUS_', '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

/** Human-readable consent type label. */
function consentTypeLabel(type: string): string {
  return (type ?? '')
    .replace('CONSENT_TYPE_', '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

const TRUST_LEVELS = [
  { value: 'FEDERATION_TRUST_LEVEL_CATALOG_ONLY', label: 'Catalog Only' },
  { value: 'FEDERATION_TRUST_LEVEL_IDENTITY', label: 'Identity' },
  { value: 'FEDERATION_TRUST_LEVEL_RESOURCE_LINK', label: 'Resource Link' },
  { value: 'FEDERATION_TRUST_LEVEL_FULL', label: 'Full' },
] as const;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PlatformFederationPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = usePlatform();
  const fedStore = usePlatformFederation();
  const { t } = useI18n();

  const [activeTab, setActiveTab] = useState<TabId>('peers');

  // Peers state
  const [peers, setPeers] = useState<any[]>([]);
  const [peersLoading, setPeersLoading] = useState(false);
  const [showAddPeer, setShowAddPeer] = useState(false);
  const [newPeer, setNewPeer] = useState({
    name: '',
    peer_endpoint: '',
    peer_platform_id: '',
    trust_level: 'FEDERATION_TRUST_LEVEL_CATALOG_ONLY',
  });

  // Discovery state
  const [discoveredPlatforms, setDiscoveredPlatforms] = useState<any[]>([]);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);

  // Consent state
  const [consents, setConsents] = useState<any[]>([]);
  const [consentsLoading, setConsentsLoading] = useState(false);

  const platform = store.currentItem;
  const platformName = platform?.name ?? platform?.slug ?? id ?? '';

  // Load platform info
  useEffect(() => {
    if (id) {
      store.GetPlatform(id);
    }
  }, [id]);

  // Load peers on mount / tab switch
  useEffect(() => {
    if (activeTab === 'peers' && id) {
      loadPeers();
    }
    if (activeTab === 'discovery') {
      loadDiscovery();
    }
    if (activeTab === 'consent') {
      loadConsents();
    }
  }, [activeTab, id]);

  const loadPeers = async () => {
    if (!id) return;
    setPeersLoading(true);
    try {
      const res = await store.ListFederationPeers(id);
      const list = res?.peers?.peers;
      setPeers(Array.isArray(list) ? list : []);
    } catch {
      // Error handled by store
    } finally {
      setPeersLoading(false);
    }
  };

  const loadDiscovery = async () => {
    setDiscoveryLoading(true);
    try {
      const res = await fedStore.DiscoverPlatforms();
      const list = res?.entries?.entries;
      setDiscoveredPlatforms(Array.isArray(list) ? list : []);
    } catch {
      // Error handled by store
    } finally {
      setDiscoveryLoading(false);
    }
  };

  const loadConsents = async () => {
    setConsentsLoading(true);
    try {
      await fedStore.ListConsentRecords({});
      setConsents(fedStore.items.slice());
    } catch {
      // Error handled by store
    } finally {
      setConsentsLoading(false);
    }
  };

  const handleCreatePeer = async () => {
    if (!id || !newPeer.name || !newPeer.peer_endpoint) return;
    try {
      await store.CreateFederationPeer(id, {
        ...newPeer,
        trust_level: newPeer.trust_level as FederationTrustLevel,
      });
      setNewPeer({
        name: '',
        peer_endpoint: '',
        peer_platform_id: '',
        trust_level: 'FEDERATION_TRUST_LEVEL_CATALOG_ONLY',
      });
      setShowAddPeer(false);
      loadPeers();
    } catch {
      // Error handled by store
    }
  };

  const handleCheckHealth = async (peerId: string) => {
    try {
      await fedStore.CheckFederationHealth(peerId, {});
      loadPeers();
    } catch {
      // Error handled by store
    }
  };

  const handleSync = async (peerId: string) => {
    try {
      await fedStore.SyncFederationCatalog(peerId, {});
      loadPeers();
    } catch {
      // Error handled by store
    }
  };

  const handleRevoke = async (peerId: string) => {
    if (!confirm(t('platform.federation.revokeConfirm'))) return;
    try {
      await fedStore.RevokeFederationPeer(peerId, {});
      loadPeers();
    } catch {
      // Error handled by store
    }
  };

  const handleConnect = async (platformId: string) => {
    if (!id) return;
    try {
      await store.CreateFederationPeer(id, {
        peer_platform_id: platformId,
        trust_level: 'FEDERATION_TRUST_LEVEL_CATALOG_ONLY',
      });
      loadPeers();
      setActiveTab('peers');
    } catch {
      // Error handled by store
    }
  };

  const handleRevokeConsent = async (consentId: string) => {
    if (!confirm(t('platform.federation.revokeConsentConfirm'))) return;
    try {
      await fedStore.RevokeDataConsent(consentId, {});
      loadConsents();
    } catch {
      // Error handled by store
    }
  };

  // Tab definitions
  const tabs: { id: TabId; label: string }[] = [
    { id: 'peers', label: t('platform.federation.tabPeers') },
    { id: 'discovery', label: t('platform.federation.tabDiscovery') },
    { id: 'consent', label: t('platform.federation.tabConsent') },
  ];

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm">
        <Link to="/platforms" className="text-muted-foreground hover:text-foreground">
          {t('platform.list')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/platforms/${id}`} className="text-muted-foreground hover:text-foreground">
          {platformName}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('platform.federation.title')}</span>
      </nav>

      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {t('platform.federation.title')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('platform.federation.subtitle')}
          </p>
        </div>
      </div>

      {/* Error */}
      {(store.error || fedStore.error) ? (
        <ErrorAlert error={store.error || fedStore.error} dismissible onDismiss={() => { store.clearError?.(); fedStore.error = null; }} />
      ) : null}

      {/* Tab navigation */}
      <div className="border-b border-border mb-6">
        <div className="flex gap-0 -mb-px">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab: Peers */}
      {activeTab === 'peers' && (
        <div className="space-y-4">
          {/* Add Peer button */}
          <div className="flex justify-end">
            <Button onClick={() => setShowAddPeer(!showAddPeer)}>
              {showAddPeer
                ? t('common.cancel')
                : t('platform.federation.addPeer')}
            </Button>
          </div>

          {/* Inline Add Peer form */}
          {showAddPeer && (
            <Card>
              <CardHeader>
                <CardTitle>{t('platform.federation.addPeerTitle')}</CardTitle>
                <CardDescription>
                  {t('platform.federation.addPeerDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground">
                      {t('platform.federation.peerName')}
                    </label>
                    <Input
                      value={newPeer.name}
                      onChange={(e) => setNewPeer({ ...newPeer, name: e.target.value })}
                      placeholder="Production EU"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground">
                      {t('platform.federation.peerEndpoint')}
                    </label>
                    <Input
                      value={newPeer.peer_endpoint}
                      onChange={(e) => setNewPeer({ ...newPeer, peer_endpoint: e.target.value })}
                      placeholder="https://api.eu.corde.io"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground">
                      {t('platform.federation.peerPlatformId')}
                    </label>
                    <Input
                      value={newPeer.peer_platform_id}
                      onChange={(e) => setNewPeer({ ...newPeer, peer_platform_id: e.target.value })}
                      placeholder="platform-uuid"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground">
                      {t('platform.federation.trustLevel')}
                    </label>
                    <Select
                      value={newPeer.trust_level}
                      onChange={(e) => setNewPeer({ ...newPeer, trust_level: e.target.value })}>
                      {TRUST_LEVELS.map((tl) => (
                        <option key={tl.value} value={tl.value}>
                          {tl.label}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setShowAddPeer(false)}>
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={handleCreatePeer}
                  disabled={!newPeer.name || !newPeer.peer_endpoint}
                >
                  {t('platform.federation.createPeer')}
                </Button>
              </CardFooter>
            </Card>
          )}

          {/* Peers table */}
          <Card>
            <Table striped hoverable>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('platform.federation.columnName')}</TableHead>
                  <TableHead>{t('platform.federation.columnEndpoint')}</TableHead>
                  <TableHead>{t('platform.federation.columnStatus')}</TableHead>
                  <TableHead>{t('platform.federation.columnTrustLevel')}</TableHead>
                  <TableHead>{t('platform.federation.columnLastHeartbeat')}</TableHead>
                  <TableHead>{t('platform.federation.columnSyncFailures')}</TableHead>
                  <TableHead className="text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {peersLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      {t('common.loading')}
                    </TableCell>
                  </TableRow>
                ) : peers.length === 0 ? (
                  <TableEmpty
                    colSpan={7}
                    message={t('platform.federation.noPeers')}
                  />
                ) : (
                  peers.map((peer: any) => {
                    const peerId = peer.entity?.id ?? peer.id ?? '';
                    const status = peer.status ?? '';
                    const isRevoked = status.includes('REVOKED');

                    return (
                      <TableRow key={peerId}>
                        <TableCell>
                          <Link
                            to={`/platforms/${id}/federation/${peerId}`}
                            className="text-sm font-medium text-primary hover:underline"
                          >
                            {peer.name ?? '--'}
                          </Link>
                          {peer.slug && (
                            <p className="text-xs text-muted-foreground">{peer.slug}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-foreground font-mono">
                            {peer.peer_endpoint ?? '--'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={peerStatusVariant(status) as any} size="sm">
                            {peerStatusLabel(status)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={trustLevelVariant(peer.trust_level ?? '') as any} size="sm">
                            {trustLevelLabel(peer.trust_level ?? '')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {relativeTime(peer.last_heartbeat)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`text-sm ${(peer.sync_failure_count ?? 0) > 0 ? 'text-destructive font-medium' : 'text-foreground'}`}>
                            {peer.sync_failure_count ?? 0}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Link to={`/platforms/${id}/federation/${peerId}`}>
                              <Button variant="ghost" size="sm">
                                {t('common.view')}
                              </Button>
                            </Link>
                            {!isRevoked && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCheckHealth(peerId)}
                                >
                                  {t('platform.federation.checkHealth')}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleSync(peerId)}
                                >
                                  {t('platform.federation.sync')}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive"
                                  onClick={() => handleRevoke(peerId)}
                                >
                                  {t('platform.federation.revoke')}
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}

      {/* Tab: Discovery */}
      {activeTab === 'discovery' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {t('platform.federation.discoveryDescription')}
            </p>
            <Button variant="secondary" onClick={loadDiscovery} disabled={discoveryLoading}>
              {discoveryLoading
                ? t('common.loading')
                : t('platform.federation.refresh')}
            </Button>
          </div>

          {discoveryLoading ? (
            <div className="flex items-center justify-center h-48">
              <div className="text-muted-foreground">{t('common.loading')}</div>
            </div>
          ) : discoveredPlatforms.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">
                  {t('platform.federation.noPlatformsFound')}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {discoveredPlatforms.map((p: any) => {
                const pId = p.entity?.id ?? p.id ?? '';
                return (
                  <Card key={pId}>
                    <CardContent className="p-5 space-y-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-sm font-semibold text-foreground">
                            {p.name ?? 'Unknown Platform'}
                          </h3>
                          {p.endpoint && (
                            <p className="text-xs text-muted-foreground font-mono mt-0.5">
                              {p.endpoint}
                            </p>
                          )}
                        </div>
                        {p.status && (
                          <Badge variant={peerStatusVariant(p.status) as any} size="sm">
                            {peerStatusLabel(p.status)}
                          </Badge>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-xs">
                        {p.region && (
                          <div>
                            <span className="text-muted-foreground">Region</span>
                            <p className="text-foreground font-medium">{p.region}</p>
                          </div>
                        )}
                        {p.product_count != null && (
                          <div>
                            <span className="text-muted-foreground">Products</span>
                            <p className="text-foreground font-medium">{p.product_count}</p>
                          </div>
                        )}
                        {p.user_count != null && (
                          <div>
                            <span className="text-muted-foreground">Users</span>
                            <p className="text-foreground font-medium">{p.user_count}</p>
                          </div>
                        )}
                      </div>

                      <div className="pt-2 border-t border-border">
                        <Button
                          size="sm"
                          onClick={() => handleConnect(pId)}
                        >
                          {t('platform.federation.connect')}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Tab: Consent */}
      {activeTab === 'consent' && (
        <div className="space-y-4">
          <Card>
            <Table striped hoverable>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('platform.federation.consentUser')}</TableHead>
                  <TableHead>{t('platform.federation.consentType')}</TableHead>
                  <TableHead>{t('platform.federation.consentPurpose')}</TableHead>
                  <TableHead>{t('platform.federation.consentStatus')}</TableHead>
                  <TableHead>{t('platform.federation.consentGrantedAt')}</TableHead>
                  <TableHead>{t('platform.federation.consentExpiresAt')}</TableHead>
                  <TableHead className="text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {consentsLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      {t('common.loading')}
                    </TableCell>
                  </TableRow>
                ) : consents.length === 0 ? (
                  <TableEmpty
                    colSpan={7}
                    message={t('platform.federation.noConsents')}
                  />
                ) : (
                  consents.map((consent: any) => {
                    const consentId = consent.entity?.id ?? consent.id ?? '';
                    const status = consent.status ?? '';
                    const isGranted = status.includes('GRANTED');

                    return (
                      <TableRow key={consentId}>
                        <TableCell>
                          <span className="text-sm text-foreground">
                            {consent.user?.display_name ?? consent.user_id ?? consent.actor?.display_name ?? '--'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" size="sm">
                            {consentTypeLabel(consent.type ?? consent.consent_type ?? '')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {consent.purpose ?? '--'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={consentStatusVariant(status) as any} size="sm">
                            {consentStatusLabel(status)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(consent.granted_at ?? consent.created_at)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(consent.expires_at)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {isGranted && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive"
                              onClick={() => handleRevokeConsent(consentId)}
                            >
                              {t('platform.federation.revokeConsent')}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}
    </div>
  );
});

export default PlatformFederationPage;
